<template lang="pug">
    v-card.pulse-disposition-comparison(
        v-if="validAiCommunicationDisposition"
        :class="{ 'pulse-disposition-comparison--compact': compact }"
        outlined
    )
        v-card-text.pa-3
            .pulse-disposition-comparison__header
                template(v-if="validPulseCallLink")
                    a.pulse-disposition-comparison__logo-link(
                        :href="pulseCallUrl"
                        aria-label="Open call in Pulse"
                        target="_blank"
                        title="Open call in Pulse"
                        rel="noopener noreferrer"
                        @click.stop
                    )
                        img.pulse-disposition-comparison__logo(:src="pulseLogoUrl" alt="Pulse")
                        span.pulse-disposition-comparison__title Pulse Disposition
                        v-icon.pulse-disposition-comparison__open-icon(size="14") mdi-open-in-new
                template(v-else)
                    img.pulse-disposition-comparison__logo(:src="pulseLogoUrl" alt="Pulse")
                    span.pulse-disposition-comparison__title Pulse Disposition
                v-spacer
                v-chip(
                    :color="dispositionMatchColor"
                    label
                    small
                    text-color="white"
                )
                    v-icon.mr-1(small) {{ dispositionMatchIcon }}
                    | {{ dispositionMatchText }}

            template(v-if="compact")
                .pulse-disposition-comparison__compact-body
                    .pulse-disposition-comparison__section
                        .pulse-disposition-comparison__label Agent
                        .pulse-disposition-comparison__value {{ displayAgentDisposition }}
                    .pulse-disposition-comparison__section
                        .pulse-disposition-comparison__label Agent Notes
                        .pulse-disposition-comparison__notes {{ displayAgentNotes }}
                    .pulse-disposition-comparison__section
                        .pulse-disposition-comparison__label Pulse
                        .pulse-disposition-comparison__value(:class="pulseDispositionClass") {{ displayPulseDisposition }}
                    template(v-if="validPulseNotes")
                        .pulse-disposition-comparison__section
                            .pulse-disposition-comparison__label Pulse Notes
                            .pulse-disposition-comparison__notes {{ selectedAiCommunicationDisposition.notes }}

            v-row(v-else dense)
                v-col(cols="12" :md="stacked ? 12 : 6")
                    .pulse-disposition-comparison__panel
                        .pulse-disposition-comparison__panel-header
                            v-icon(size="26") mdi-face-agent
                            span Agent
                        .pulse-disposition-comparison__section
                            .pulse-disposition-comparison__label Disposition
                            .pulse-disposition-comparison__value {{ displayAgentDisposition }}
                        .pulse-disposition-comparison__section
                            .pulse-disposition-comparison__label Notes
                            .pulse-disposition-comparison__notes {{ displayAgentNotes }}

                v-col(cols="12" :md="stacked ? 12 : 6")
                    .pulse-disposition-comparison__panel.pulse-disposition-comparison__panel--pulse
                        .pulse-disposition-comparison__panel-header
                            v-icon(size="26") mdi-robot-outline
                            span Pulse
                        .pulse-disposition-comparison__section
                            .pulse-disposition-comparison__label Disposition
                            .pulse-disposition-comparison__value(:class="pulseDispositionClass") {{ displayPulseDisposition }}
                        .pulse-disposition-comparison__section
                            .pulse-disposition-comparison__label Notes
                            .pulse-disposition-comparison__notes {{ displayPulseNotes }}
</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Helpers
    import { currentUserIsManagerOrAdmin } from '@/helpers/user';

    type AiCommunicationDisposition = {
        disposition_description?: string;
        id?: number;
        notes?: string;
    }

    export default Vue.extend({
        computed: {
            displayAgentDisposition() {
                return this.agentDisposition || 'N/a';
            },
            displayAgentNotes() {
                return this.agentNotes || 'N/a';
            },
            displayPulseDisposition() {
                return this.selectedAiCommunicationDisposition?.disposition_description || 'N/a';
            },
            displayPulseNotes() {
                return this.selectedAiCommunicationDisposition?.notes || 'N/a';
            },
            dispositionMatchColor() {
                return this.dispositionMatches ? 'success' : 'error';
            },
            dispositionMatchIcon() {
                return this.dispositionMatches ? 'mdi-check' : 'mdi-close';
            },
            dispositionMatches() {
                return this.normalizeDisposition(this.agentDisposition) === this.normalizeDisposition(this.selectedAiCommunicationDisposition?.disposition_description);
            },
            dispositionMatchText() {
                return this.dispositionMatches ? 'Match' : 'Mismatch';
            },
            pulseDispositionClass() {
                return this.dispositionMatches ? 'success--text' : 'error--text';
            },
            pulseCallUrl() {
                const baseUrl = VolieState.basePulseV2Url();
                if (!(baseUrl?.length > 0) || !(this.conferenceVid?.length > 0)) {
                    return '';
                }

                return `${baseUrl.replace(/\/$/, '')}/pulse/${this.conferenceVid}`;
            },
            selectedAiCommunicationDisposition(): AiCommunicationDisposition {
                return this.aiCommunicationDispositions[0] || {};
            },
            validAiCommunicationDisposition() {
                return this.selectedAiCommunicationDisposition?.id > 0;
            },
            validPulseCallLink() {
                return currentUserIsManagerOrAdmin() && this.pulseCallUrl.length > 0;
            },
            validPulseNotes() {
                return this.selectedAiCommunicationDisposition?.notes?.length > 0;
            },
        },
        methods: {
            normalizeDisposition(value?: string) {
                return (value || '').trim().toLowerCase();
            },
        },
        props: {
            agentDisposition: {
                type: String,
                default: '',
            },
            agentNotes: {
                type: String,
                default: '',
            },
            aiCommunicationDispositions: {
                type: Array as () => AiCommunicationDisposition[],
                default: () => [],
            },
            compact: {
                type: Boolean,
                default: false,
            },
            conferenceVid: {
                type: String,
                default: '',
            },
            pulseLogoUrl: {
                type: String,
                default: 'https://volie-assets.s3.us-east-1.amazonaws.com/system/pulse_branding/PulsePrimaryLogo_Light_Icon.png',
            },
            stacked: {
                type: Boolean,
                default: false,
            },
        },
    });
</script>

<style lang="scss" scoped>
    .pulse-disposition-comparison {
        background: linear-gradient(160deg, #ffffff, #f8fafe);
        border-color: rgba(64, 105, 170, 0.2) !important;

        &__compact-body {
            display: grid;
            gap: 10px;
        }

        &__header {
            align-items: center;
            display: flex;
            gap: 8px;
            margin-bottom: 12px;
            min-width: 0;
        }

        &__label {
            color: rgba(0, 0, 0, 0.54);
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
        }

        &__logo {
            height: 24px;
            object-fit: contain;
            width: 24px;
        }

        &__logo-link {
            align-items: center;
            border-radius: 4px;
            color: inherit;
            cursor: pointer;
            display: flex;
            gap: 6px;
            line-height: 1.25;
            text-decoration: none;
            transition: opacity 120ms ease, transform 120ms ease;

            &:focus-visible {
                outline: 2px solid rgba(64, 105, 170, 0.45);
                outline-offset: 2px;
            }

            &:hover {
                opacity: 0.82;
                transform: translateY(-1px);
            }
        }

        &__notes {
            font-size: 13px;
            line-height: 1.45;
            white-space: pre-wrap;
        }

        &__open-icon {
            color: rgba(64, 105, 170, 0.8);
        }

        &__panel {
            border: 1px solid rgba(64, 105, 170, 0.14);
            border-radius: 10px;
            height: 100%;
            padding: 12px;

            &--pulse {
                background: rgba(64, 105, 170, 0.04);
            }
        }

        &__panel-header {
            align-items: center;
            display: flex;
            font-weight: 700;
            gap: 8px;
            margin-bottom: 12px;
        }

        &__section {
            margin-bottom: 10px;
        }

        &__title {
            font-size: 14px;
            font-weight: 800;
            letter-spacing: 0.4px;
            line-height: 1.25;
            text-transform: uppercase;
        }

        &__value {
            font-size: 14px;
            font-weight: 700;
        }

        &--compact {
            margin: 8px 0 10px;
            width: 100%;
        }
    }
</style>
